(() => {
  const SHELL = '.khushi-product-gallery-shell[data-khushi-enabled="true"]';
  const SLIDE = 'slideshow-slide';
  const VIDEO_SLIDE = '.product-media-container--video, .product-media-container--external_video';
  const EXTERNAL_PLAYER = 'iframe[data-video-type], iframe.js-youtube, iframe.js-vimeo';
  const FINE = matchMedia('(hover: hover) and (pointer: fine)');
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const ready = new WeakSet();
  const zoomState = new WeakMap();

  const activeSlide = (shell) =>
    shell.querySelector(`${SLIDE}[aria-hidden="false"]`) ||
    Array.from(shell.querySelectorAll(SLIDE)).find((slide) => !slide.hidden) ||
    null;

  function configureVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
  }

  function externalPlayerType(iframe) {
    if (!(iframe instanceof HTMLIFrameElement)) return null;
    if (iframe.dataset.videoType === 'youtube' || iframe.classList.contains('js-youtube') || iframe.src.includes('youtube')) return 'youtube';
    if (iframe.dataset.videoType === 'vimeo' || iframe.classList.contains('js-vimeo') || iframe.src.includes('vimeo')) return 'vimeo';
    return null;
  }

  function controlExternalPlayer(iframe, action) {
    if (!(iframe instanceof HTMLFrameElement)) return;
    const type = externalPlayerType(iframe);
    if (type === 'youtube') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' }),
        '*'
      );
    } else if (type === 'vimeo') {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: action }), '*');
    }
  }

  function normalizePlayables(media) {
    if (!(media instanceof HTMLElement)) return;

    const videos = Array.from(media.querySelectorAll('video'));
    videos.slice(1).forEach((video) => {
      video.pause();
      video.remove();
    });

    const iframes = Array.from(media.querySelectorAll(EXTERNAL_PLAYER));
    iframes.slice(1).forEach((iframe) => {
      controlExternalPlayer(iframe, 'pause');
      iframe.remove();
    });
  }

  function ensurePlayable(media) {
    if (!(media instanceof HTMLElement)) return null;
    normalizePlayables(media);

    let playable = media.querySelector(`video, ${EXTERNAL_PLAYER}`);
    if (!playable && !media.hasAttribute('data-media-loaded') && typeof media.loadContent === 'function') {
      media.loadContent(false);
      normalizePlayables(media);
      playable = media.querySelector(`video, ${EXTERNAL_PLAYER}`);
    }

    return playable;
  }

  function stopMedia(media) {
    if (!(media instanceof HTMLElement)) return;
    normalizePlayables(media);

    const video = media.querySelector('video');
    const iframe = media.querySelector(EXTERNAL_PLAYER);

    if (video instanceof HTMLVideoElement) video.pause();
    if (iframe instanceof HTMLFrameElement) controlExternalPlayer(iframe, 'pause');
    if (!(video instanceof HTMLVideoElement) && !(iframe instanceof HTMLIFrameElement) && typeof media.pauseMedia === 'function') {
      media.pauseMedia();
    }
  }

  function startMedia(media) {
    if (!(media instanceof HTMLElement)) return;
    const playable = ensurePlayable(media);

    if (playable instanceof HTMLVideoElement) {
      configureVideo(playable);
      playable.play().catch(() => {});
      return;
    }

    if (playable instanceof HTMLFrameElement) {
      controlExternalPlayer(playable, 'play');
      return;
    }

    if (typeof media.playMedia === 'function') media.playMedia();
  }

  function syncVideos(shell) {
    const current = activeSlide(shell);
    shell.querySelectorAll(`${SLIDE} deferred-media`).forEach((media) => {
      const slide = media.closest(SLIDE);
      if (slide === current && slide?.matches(VIDEO_SLIDE)) startMedia(media);
      else stopMedia(media);
    });
  }

  function state(shell) {
    let value = zoomState.get(shell);
    if (value) return value;
    value = { image: null, x: 0, y: 0, scale: 1, tx: 0, ty: 0, ts: 1, raf: 0 };
    zoomState.set(shell, value);
    return value;
  }

  function render(shell) {
    const s = state(shell);
    if (!(s.image instanceof HTMLImageElement)) {
      s.raf = 0;
      return;
    }

    const factor = REDUCED.matches ? 1 : 0.14;
    s.x += (s.tx - s.x) * factor;
    s.y += (s.ty - s.y) * factor;
    s.scale += (s.ts - s.scale) * factor;

    if (Math.abs(s.tx - s.x) < 0.02) s.x = s.tx;
    if (Math.abs(s.ty - s.y) < 0.02) s.y = s.ty;
    if (Math.abs(s.ts - s.scale) < 0.002) s.scale = s.ts;

    s.image.style.setProperty('--khushi-zoom-x', `${s.x.toFixed(2)}%`);
    s.image.style.setProperty('--khushi-zoom-y', `${s.y.toFixed(2)}%`);
    s.image.style.setProperty('--khushi-zoom-scale', s.scale.toFixed(4));

    if (Math.abs(s.tx - s.x) >= 0.02 || Math.abs(s.ty - s.y) >= 0.02 || Math.abs(s.ts - s.scale) >= 0.002) {
      s.raf = requestAnimationFrame(() => render(shell));
    } else {
      s.raf = 0;
    }
  }

  function requestRender(shell) {
    const s = state(shell);
    if (!s.raf) s.raf = requestAnimationFrame(() => render(shell));
  }

  function clearZoom(shell, immediate = false) {
    const s = state(shell);
    s.image?.closest(SLIDE)?.removeAttribute('data-khushi-zoom-active');
    s.tx = 0;
    s.ty = 0;
    s.ts = 1;

    if (immediate && s.image) {
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
      s.x = 0;
      s.y = 0;
      s.scale = 1;
      s.image.style.setProperty('--khushi-zoom-x', '0%');
      s.image.style.setProperty('--khushi-zoom-y', '0%');
      s.image.style.setProperty('--khushi-zoom-scale', '1');
      s.image = null;
    } else {
      requestRender(shell);
    }
  }

  function pointerMove(shell, event) {
    if (!FINE.matches || REDUCED.matches) return;
    if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const mediaBox = target.closest('.product-media');
    const slide = mediaBox?.closest(`${SLIDE}.product-media-container--image`);
    if (!(mediaBox instanceof HTMLElement) || !(slide instanceof HTMLElement) || slide !== activeSlide(shell)) {
      clearZoom(shell);
      return;
    }

    const image = mediaBox.querySelector('.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const s = state(shell);
    if (s.image && s.image !== image) clearZoom(shell, true);
    s.image = image;
    slide.setAttribute('data-khushi-zoom-active', 'true');

    const rect = mediaBox.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    const scale = 1.8;
    const travel = ((scale - 1) / scale) * 100;

    s.tx = (0.5 - x) * travel;
    s.ty = (0.5 - y) * travel;
    s.ts = scale;
    requestRender(shell);
  }

  function init(shell) {
    if (!(shell instanceof HTMLElement) || ready.has(shell)) return;
    ready.add(shell);

    shell.addEventListener('pointermove', (event) => pointerMove(shell, event), { passive: true });
    shell.addEventListener('pointerleave', () => clearZoom(shell));
    shell.addEventListener('pointercancel', () => clearZoom(shell));
    shell.addEventListener('slideshow:select', () => requestAnimationFrame(() => {
      clearZoom(shell, true);
      syncVideos(shell);
    }));

    new MutationObserver(() => requestAnimationFrame(() => syncVideos(shell))).observe(shell, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'hidden', 'reveal', 'data-media-loaded'],
    });

    requestAnimationFrame(() => syncVideos(shell));
  }

  function initAll(root = document) {
    if (root instanceof Element && root.matches(SHELL)) init(root);
    root.querySelectorAll?.(SHELL).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  Promise.all([customElements.whenDefined('deferred-media'), customElements.whenDefined('slideshow-component')]).then(() => {
    document.querySelectorAll(SHELL).forEach((shell) => {
      init(shell);
      syncVideos(shell);
    });
  });
})();
